import {
  AudioExportError, AudioExportJob, AudioMetadata, AUDIO_BITRATES, MAX_AUDIO_OUTPUT_BYTES,
} from './audioExport';

export const MAX_VIDEO_BYTES = 512 * 1024 * 1024;
export const VIDEO_AUDIO_EXTENSIONS = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'] as const;
export interface VideoAudioMetadata extends AudioMetadata { codec: string }

export const validateVideoFile = (file: Pick<File, 'name' | 'size'>): void => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  if (!(VIDEO_AUDIO_EXTENSIONS as readonly string[]).includes(extension)) throw new AudioExportError('invalidFile');
  if (file.size === 0) throw new AudioExportError('emptyFile');
  if (file.size > MAX_VIDEO_BYTES) throw new AudioExportError('fileTooLarge');
};

interface ProbeStream {
  codec_type?: string;
  codec_name?: string;
  duration?: string | number;
  channels?: number;
  sample_rate?: string;
  disposition?: { attached_pic?: number };
}

export const parseVideoAudioMetadata = (json: string): VideoAudioMetadata => {
  let streams: ProbeStream[];
  try {
    const data = JSON.parse(json);
    if (!Array.isArray(data.streams)) throw new Error('Missing streams');
    streams = data.streams;
    if (!streams.some((stream) => stream?.codec_type === 'video' && stream.disposition?.attached_pic !== 1)) {
      throw new Error('Missing video');
    }
  } catch {
    throw new AudioExportError('invalidVideo');
  }
  const audio = streams.find((stream) => stream?.codec_type === 'audio');
  if (!audio) throw new AudioExportError('noAudio');
  const channels = Number(audio.channels);
  const sampleRate = Number(audio.sample_rate);
  if (!Number.isInteger(channels) || channels < 1 || channels > 64
      || !Number.isFinite(sampleRate) || sampleRate <= 0 || !audio.codec_name || audio.codec_name === 'unknown') {
    throw new AudioExportError('invalidAudio');
  }
  const duration = Number(audio.duration);
  // A container duration may describe longer video; only trust this audio track.
  return {
    duration: Number.isFinite(duration) && duration > 0 ? duration : null,
    channels, sampleRate, codec: audio.codec_name,
  };
};

export const buildVideoToAudioArgs = (bitrate: number, metadata: VideoAudioMetadata, inputPath: string): string[] => {
  if (!(AUDIO_BITRATES as readonly number[]).includes(bitrate)) throw new AudioExportError('invalidBitrate');
  return [
    '-hide_banner', '-xerror', '-err_detect', 'explode', '-i', inputPath,
    '-map', '0:a:0', '-vn', '-sn', '-dn', '-map_metadata', '0', '-map_chapters', '-1',
    '-c:a', 'libmp3lame', '-b:a', `${bitrate}k`,
    ...(metadata.channels > 2 ? ['-ac', '2'] : []),
    '-id3v2_version', '3', '-fs', String(MAX_AUDIO_OUTPUT_BYTES), '/output.mp3',
  ];
};

export class VideoToAudioJob extends AudioExportJob<VideoAudioMetadata> {
  constructor() {
    super({
      validateFile: validateVideoFile,
      inputName: (file) => `source.${file.name.split('.').pop()!.toLowerCase()}`,
      parseMetadata: parseVideoAudioMetadata,
      buildArgs: buildVideoToAudioArgs,
    });
  }
}
